'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useSite } from '@/contexts/SiteContext';
import type { Event_Entry } from '@/types/graphql';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/dateFormat';
import {
    getShowHeaderImage,
    getShowPageLink,
    getShowProgramEnd,
    getShowProgramStart,
} from '@/utils/show';

// Define the Value type for react-calendar
type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

interface FloatingCalendarButtonProps {
    events: Event_Entry[];
}

/**
 * Format a local calendar date (from react-calendar) as YYYY-MM-DD.
 * This uses the date's local year/month/day values directly without timezone conversion,
 * since react-calendar tiles represent abstract calendar dates, not specific moments in time.
 */
const formatCalendarDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export default function FloatingCalendarButton({ events }: FloatingCalendarButtonProps) {
    const { locale } = useSite();
    const t = useTranslations('FloatingCalendar');
    const [mounted, setMounted] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [open, setOpen] = useState(false);

    // Only render the Sheet after mount to avoid Radix UI hydration mismatch
    useEffect(() => {
        setMounted(true);
        setSelectedDate(new Date());
    }, []);

    // Create a map of dates that have events (event dates converted to venue timezone)
    const eventDates = new Set(
        events.map((event) => {
            const startDate = getShowProgramStart(event);
            // Event dates are converted to venue timezone for comparison
            return formatDate.compareFormat(startDate, locale);
        })
    );

    // Filter events for the selected date
    // selectedDate from react-calendar is a local date representing the calendar tile clicked
    // We compare it against event dates in venue timezone
    const selectedDateEvents = selectedDate
        ? events.filter((event) => {
              const startDate = getShowProgramStart(event);
              const eventDateStr = formatDate.compareFormat(startDate, locale);
              const selectedDateStr = formatCalendarDate(selectedDate);
              return eventDateStr === selectedDateStr;
          })
        : [];

    const handleDateChange = (value: Value) => {
        if (value instanceof Date) {
            setSelectedDate(value);
        }
    };

    // Don't render until mounted to avoid Radix hydration issues
    if (!mounted) {
        return null;
    }

    const calendarValue = selectedDate ?? new Date();

    return (
        <div className="fixed right-4 bottom-4 z-50 font-neue-haas">
            {/* Calendar Button with Sheet */}
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                    <button
                        type="button"
                        className={cn(
                            'rounded-full bg-haug-coral-100 p-4 text-haug-ivory-100 shadow-lg',
                            'transition-colors duration-200 hover:bg-haug-coral-70',
                            'flex items-center justify-center'
                        )}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <title>Calendar</title>
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                    </button>
                </SheetTrigger>
                <SheetContent className="h-full max-h-screen w-full overflow-y-auto bg-haug-ivory-100 p-4 pt-12 font-neue-haas sm:w-[90%] md:w-[450px]">
                    <SheetTitle className="sr-only">{t('title')}</SheetTitle>
                    <div className="mt-2">
                        <Calendar
                            onChange={handleDateChange}
                            value={calendarValue}
                            locale={locale}
                            prev2Label={null}
                            next2Label={null}
                            tileClassName={({ date }: { date: Date }) => {
                                // Use local date format for calendar tiles (not venue timezone)
                                // since react-calendar tiles represent abstract calendar dates
                                const dateStr = formatCalendarDate(date);
                                return eventDates.has(dateStr) ? 'has-event' : '';
                            }}
                            className="mb-6 w-full border-none"
                        />

                        {/* Selected Date Events */}
                        {selectedDate && selectedDateEvents.length > 0 && (
                            <div className="mt-4 space-y-3">
                                <h3 className="mb-3 font-bold text-haug-lila-100">
                                    {formatDate.fullDayMonthYearLocal(selectedDate, locale)}
                                </h3>
                                <div>
                                    {selectedDateEvents.map((event, idx) => {
                                        const image = getShowHeaderImage(event);
                                        const endTime = getShowProgramEnd(event);

                                        return (
                                            <Link
                                                key={event.id}
                                                href={getShowPageLink(event)}
                                                onClick={() => {
                                                    setOpen(false);
                                                }}
                                                aria-label="Buy Tickets"
                                                className={cn(
                                                    'flex items-center gap-3 rounded-lg transition-colors duration-200',
                                                    'bg-haug-ivory-30 hover:bg-haug-lila-70',
                                                    idx !== 0 && 'mt-3',
                                                    'w-full overflow-hidden'
                                                )}
                                            >
                                                {image?.urlSmall && (
                                                    <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded">
                                                        <Image
                                                            src={image.urlSmall}
                                                            alt={event.eventTitle || ''}
                                                            fill
                                                            sizes="64px"
                                                            className="object-cover"
                                                            style={{ mixBlendMode: 'luminosity' }}
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex min-w-0 flex-grow flex-col py-1">
                                                    <div
                                                        className="whitespace-wrap mb-0.5 font-bold text-base"
                                                        title={event.eventTitle || ''}
                                                    >
                                                        {event.eventTitle}
                                                    </div>
                                                    <div className="mb-0.5 text-haug-choco-100 text-xs">
                                                        {formatDate.time(
                                                            getShowProgramStart(event),
                                                            locale
                                                        )}
                                                        {endTime &&
                                                            ` - ${formatDate.time(endTime, locale)}`}
                                                    </div>
                                                </div>
                                                <div className="m-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-haug-coral-100 text-haug-ivory-100 shadow transition-colors duration-200 hover:bg-haug-coral-70">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                        className="h-4 w-4"
                                                    >
                                                        <title>Buy Tickets</title>
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M9 5l7 7-7 7"
                                                        />
                                                    </svg>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}
